import React from 'react';
import type { SqueezoHistoryPoint } from '../types/squeezo';
import { getSqueezoGaugeColor, getSqueezoGaugeColorRgb } from '../utils/squeezoColors';

interface SqueezoSparklineProps {
  data: SqueezoHistoryPoint[];
  onClick: () => void;
  /**
   * Paint the whole line in one colour instead of per-segment.
   *
   * The header gauge passes its zone colour so the control reads as one
   * object. Left undefined, each segment keeps its own gradient colour, which
   * is what the full Squeezo widget wants — there is room there to read it.
   */
  color?: string;
}

const WIDTH = 120;
const HEIGHT = 25;
const PADDING_Y = 3;

export const SqueezoSparkline: React.FC<SqueezoSparklineProps> = ({ data, onClick, color }) => {
  if (data.length < 2) return null;

  // Data arrives newest-first; reverse so left = oldest, right = newest
  const points = [...data].reverse();

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const xStep = WIDTH / (points.length - 1);
  const yScale = (v: number): number =>
    HEIGHT - PADDING_Y - ((v - min) / range) * (HEIGHT - PADDING_Y * 2);

  // Build coordinate pairs
  const coords = points.map((p, i) => ({
    x: i * xStep,
    y: yScale(p.value),
    value: p.value,
  }));

  // Fill polygon points (line path + bottom edge)
  const fillPoints = coords.map((c) => `${c.x},${c.y}`).join(' ')
    + ` ${WIDTH},${HEIGHT} 0,${HEIGHT}`;

  // Average color for fill area
  const avgValue = values.reduce((a, b) => a + b, 0) / values.length;
  const { r, g, b } = getSqueezoGaugeColorRgb(avgValue);
  // A flat colour fills with itself; the gradient keeps its averaged tint.
  const fill = color ? `${color}26` : `rgba(${r}, ${g}, ${b}, 0.15)`;

  return (
    <svg
      width="100%"
      height="20"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      className="cursor-pointer"
      onClick={onClick}
      role="img"
      aria-label="Squeezo sparkline — click to expand"
    >
      {/* Subtle fill */}
      <polygon
        points={fillPoints}
        fill={fill}
      />

      {/* Colored line segments */}
      {coords.map((coord, i) => {
        if (i === 0) return null;
        const prev = coords[i - 1];
        // Color based on the endpoint value of each segment
        return (
          <line
            key={i}
            x1={prev.x}
            y1={prev.y}
            x2={coord.x}
            y2={coord.y}
            stroke={color ?? getSqueezoGaugeColor(coord.value)}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
};
